/**
 * Build modelAPI data-source query filters for task data-table blocks.
 * The workflow uses ``data_table`` naming; modelAPI paths still say ``data_source``.
 */

/** Unit separator for ``in`` / ``not_in`` filter values (matches modelAPI predicate IR). */
export const DATA_TABLE_MULTI_VALUE_SEP = '\u001F';

export type DataTableScopeValueSource = 'dimension_item_name' | 'dimension_item_id';

export interface DataTableScopeFilterSpec {
  column: string;
  op?: string;
  value_from?: DataTableScopeValueSource;
}

export interface TaskDimensionContext {
  dimension_item_name: string;
  dimension_item_id: number;
}

/** One user-applied column filter encoded as a single modelAPI filter param. */
export type UserColumnFilter = { op: 'in'; values: string[] } | { op: 'not_in'; values: string[] };

/** User-visible column filters (Excel AutoFilter: OR within column, AND across columns). */
export type UserColumnFilters = Record<string, UserColumnFilter>;

export interface DataTableQueryState {
  scopeFilters: string[];
  userFilters: UserColumnFilters;
  sort: { column: string; direction: 'asc' | 'desc' } | null;
}

const escapeFilterValue = (value: string) => value.replace(/\\/g, '\\\\');

const encodeMultiValue = (values: string[]) =>
  values.map((value) => escapeFilterValue(value)).join(DATA_TABLE_MULTI_VALUE_SEP);

/** Resolve fixed scope filters from the task row. */
export const buildScopeFilterParameters = (
  scopes: DataTableScopeFilterSpec[],
  task: TaskDimensionContext,
): string[] =>
  scopes.map((scope) => {
    const op = scope.op || 'eq';
    const source = scope.value_from || 'dimension_item_name';
    const raw =
      source === 'dimension_item_id' ? String(task.dimension_item_id) : task.dimension_item_name;
    return `${scope.column}:${op}:${escapeFilterValue(raw)}`;
  });

/** One modelAPI filter param for a stored user column filter. */
export const userColumnFilterParameter = (
  column: string,
  filter: UserColumnFilter | undefined,
): string | null => {
  if (!filter || filter.values.length === 0) return null;
  if (filter.op === 'in') {
    if (filter.values.length === 1) return `${column}:eq:${escapeFilterValue(filter.values[0])}`;
    return `${column}:in:${encodeMultiValue(filter.values)}`;
  }
  if (filter.values.length === 1) return `${column}:ne:${escapeFilterValue(filter.values[0])}`;
  return `${column}:not_in:${encodeMultiValue(filter.values)}`;
};

/**
 * Pick ``in`` vs ``not_in`` from facet checkboxes: exclude a few values with ``not_in``
 * when that is a smaller payload than listing everything else.
 */
export const chooseColumnFilterFromFacetSelection = (
  facetValues: string[],
  checkedValues: string[],
): UserColumnFilter | null => {
  if (facetValues.length === 0) return null;
  const facetSet = new Set(facetValues);
  const checked = checkedValues.filter((value) => facetSet.has(value));
  if (checked.length === facetValues.length) return null;

  const excluded = facetValues.filter((value) => !checked.includes(value));
  if (checked.length === 0) {
    return excluded.length === 1
      ? { op: 'not_in', values: excluded }
      : { op: 'not_in', values: excluded };
  }
  if (excluded.length > 0 && excluded.length < checked.length) {
    return { op: 'not_in', values: excluded };
  }
  return { op: 'in', values: checked };
};

/** Checked boxes to show when reopening a column filter menu. */
export const checkedValuesForFacetMenu = (
  filter: UserColumnFilter | undefined,
  facetValues: string[],
): string[] => {
  if (!filter) return [...facetValues];
  if (filter.op === 'in') return filter.values.filter((value) => facetValues.includes(value));
  return facetValues.filter((value) => !filter.values.includes(value));
};

/** Short label for filter chips above the grid. */
export const userFilterChipText = (filter: UserColumnFilter): string => {
  if (filter.op === 'not_in') {
    return `Excluding ${filter.values.join(', ')}`;
  }
  return filter.values.join(', ');
};

/** Merge scope and user filters for query / distinct-values calls. */
export const buildCombinedFilterParameters = (state: DataTableQueryState): string[] => {
  const user = Object.entries(state.userFilters)
    .map(([column, filter]) => userColumnFilterParameter(column, filter))
    .filter((item): item is string => item !== null);
  return [...state.scopeFilters, ...user];
};

export const sortParameter = (sort: DataTableQueryState['sort']): string | undefined =>
  sort ? `${sort.column}:${sort.direction}` : undefined;

/** Filters sent when loading distinct values for one column (excludes that column's user filter). */
export const buildFacetFilterParameters = (
  state: DataTableQueryState,
  column: string,
): string[] => {
  const withoutColumn: DataTableQueryState = {
    ...state,
    userFilters: Object.fromEntries(
      Object.entries(state.userFilters).filter(([key]) => key !== column),
    ),
  };
  return buildCombinedFilterParameters(withoutColumn);
};

export interface DataTableColumnSpec {
  name: string;
  label?: string | null;
}

/** Column list from spec projection or first query response. */
export const resolveDisplayColumns = (
  specColumns: DataTableColumnSpec[],
  responseColumns: string[],
): DataTableColumnSpec[] => {
  if (specColumns.length > 0) return specColumns;
  return responseColumns.map((name) => ({ name, label: name }));
};

export const columnLabel = (columns: DataTableColumnSpec[], name: string): string =>
  columns.find((item) => item.name === name)?.label || name;

export const columnsQueryParameter = (columns: DataTableColumnSpec[]): string | undefined => {
  if (columns.length === 0) return undefined;
  return columns.map((item) => item.name).join(',');
};

/** @deprecated Use DATA_TABLE_MULTI_VALUE_SEP */
export const DATA_TABLE_IN_SEP = DATA_TABLE_MULTI_VALUE_SEP;
